/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { WBLProvider, useWBL } from './context/WBLContext';
import { ThemeManager } from './components/ThemeManager';
import { Header } from './components/Common/Header';
import { FerpaConsentModal } from './components/Common/FerpaConsentModal';
import { InAppDialogProvider } from './components/Common/InAppDialog';
import { ProxyFloatingIndicator } from './components/Common/ProxyFloatingIndicator';
import { calculateAge } from './utils/timeUtils';

// Dashboards
import { SuperadminDashboard } from './components/Dashboards/SuperadminDashboard';
import { RegionalAdminDashboard } from './components/Dashboards/RegionalAdminDashboard';
import { AdvisorDashboard } from './components/Dashboards/AdvisorDashboard';
import { OrganizationDashboard } from './components/Dashboards/OrganizationDashboard';
import { StudentDashboard } from './components/Dashboards/StudentDashboard';
import { ParentDashboard } from './components/Dashboards/ParentDashboard';
import { OpportunitiesPage } from './components/Dashboards/OpportunitiesPage';
import { SuperadminConsole } from './components/Dashboards/SuperadminConsole';
import { RegionalAdminConsole } from './components/Dashboards/RegionalAdminConsole';
import { StudentApplications } from './components/Dashboards/StudentApplications';
import { PlacementsPage } from './components/Dashboards/PlacementsPage';
import { PlacementDetail } from './components/Dashboards/PlacementDetail';
import { AdviseesPage } from './components/Dashboards/AdviseesPage';
import { AdvisorsPage } from './components/Dashboards/AdvisorsPage';
import { InterviewsPage } from './components/Dashboards/InterviewsPage';
import { MessagesPage } from './components/Dashboards/MessagesPage';
import { SettingsPage } from './components/Dashboards/SettingsPage';
import { RegionalUsersPage } from './components/Dashboards/RegionalUsersPage';
import { SuperadminUsersPage } from './components/Dashboards/SuperadminUsersPage';
import { FoundationsPage } from './components/Dashboards/FoundationsPage';
import { ReportsPage } from './components/Reports/ReportsPage';

// Lucide icons
import { GraduationCap, Key, Loader2, Globe, Sparkles, HelpCircle, MessageSquare, Scale, ShieldCheck } from 'lucide-react';

// Landing Page
import { LandingPage } from './components/LandingPage';
import { SetupWizard } from './components/Onboarding/SetupWizard';
import { PasswordResetWizard } from './components/Onboarding/PasswordResetWizard';
import { ParentOnboardingPage } from './components/Onboarding/ParentOnboarding';
import { OrgTeamOnboardingPage } from './components/Onboarding/OrgTeamOnboarding';
import { ForgotPasswordPage } from './components/Onboarding/ForgotPasswordPage';
import { ResetPasswordPage } from './components/Onboarding/ResetPasswordPage';
import { UserProfile } from './types';

import { GlobalFooter } from './components/Common/GlobalFooter';

function isProfileIncomplete(user: UserProfile | null): boolean {
  if (!user) return false;
  if (user.role === 'superadmin') return false;
  
  // If the profileComplete flag is explicitly true, they are not incomplete
  if (user.profileComplete === true) return false;

  // If the profileComplete flag is explicitly false, it is definitely incomplete
  if (user.profileComplete === false) return true;

  // Enforce critical credential data checks to ensure compliance
  const hasName = user.name && user.name.trim() !== '' && user.name !== 'Authorized Gateway User';
  const hasContactInfo = user.contactFirstName && user.contactLastName;

  if (!hasName && !hasContactInfo) {
    return true;
  }

  if (user.role === 'student') {
    if (
      !user.dateOfBirth ||
      !user.school ||
      !user.classYear ||
      !user.district
    ) {
      return true;
    }
  } else if (user.role === 'organization') {
    if (!user.orgName) {
      return true;
    }
  } else if (user.role === 'parent') {
    if (!user.childId && !user.contactPhone) {
      return true;
    }
  }

  // If the profileComplete field is missing/undefined and some critical info is absent, it's incomplete
  if (user.profileComplete === undefined) {
    return true;
  }

  return false;
}

const OrganizationProfile = React.lazy(() => import('./components/Dashboards/OrganizationProfile').then(module => ({ default: module.OrganizationProfile })));

function DashboardRouter() {
  const { activeUser, isLoading, currentView, setCurrentView, grantFerpaConsent, signOutUser } = useWBL();

  const [currentPath, setCurrentPath] = React.useState(typeof window !== 'undefined' ? window.location.pathname : '/');

  React.useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);

    (window as any).smoothNavigate = (path: string) => {
      window.history.pushState({}, '', path);
      setCurrentPath(path);
    };

    return () => {
      window.removeEventListener('popstate', handlePopState);
      delete (window as any).smoothNavigate;
    };
  }, []);

  const userAge = calculateAge(activeUser?.dateOfBirth);
  const isMinorStudent = activeUser?.role === 'student' && userAge < 18;

  const showFerpa = activeUser && 
    (activeUser.role === 'student' || activeUser.role === 'parent') && 
    (!activeUser.ferpaConsent || activeUser.ferpaConsent.granted === false) &&
    !isMinorStudent &&
    currentView !== 'settings';

  if (isLoading) {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans text-xs">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
        </div>
      </main>
    );
  }

  // Intercept and render dedicated authentication and onboarding routes
  const hasJoinOrgParam = typeof window !== 'undefined' && (
    new URLSearchParams(window.location.search).get('joinOrg') === '1' ||
    new URLSearchParams(window.location.search).get('orgInvite') === 'true'
  );
  const isParentOnboardingPath = currentPath === '/parent-onboarding' || currentPath.startsWith('/parent-onboarding');
  const isOrgOnboardingPath =
    !hasJoinOrgParam && (
      currentPath === '/org-onboarding' ||
      currentPath.startsWith('/org-onboarding') ||
      currentPath.endsWith('/org-onboarding')
    );
  const isForgotPasswordPath = currentPath === '/forgot-password' || currentPath.startsWith('/forgot-password');
  const isResetPasswordPath = currentPath === '/reset-password' || currentPath.startsWith('/reset-password');

  return (
    <AnimatePresence mode="wait">
      {isForgotPasswordPath ? (
        <motion.div
          key="forgot-password"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="min-h-screen bg-slate-950"
        >
          <ForgotPasswordPage />
        </motion.div>
      ) : isResetPasswordPath ? (
        <motion.div
          key="reset-password"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="min-h-screen bg-slate-950"
        >
          <ResetPasswordPage />
        </motion.div>
      ) : isOrgOnboardingPath ? (
        <motion.div
          key="org-onboarding"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="min-h-screen bg-slate-950"
        >
          <OrgTeamOnboardingPage />
        </motion.div>
      ) : isParentOnboardingPath ? (
        <motion.div
          key="parent-onboarding"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="min-h-screen bg-slate-950"
        >
          <ParentOnboardingPage />
        </motion.div>
      ) : !activeUser ? (
        <motion.div
          key="landing"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="min-h-screen"
        >
          <LandingPage />
        </motion.div>
      ) : (
        <motion.div
          key="authenticated"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="h-screen bg-slate-50 text-slate-800 flex flex-col relative font-sans overflow-hidden"
        >
          <Header />
          
          {showFerpa && (
            <FerpaConsentModal 
              onGrant={(sigData) => grantFerpaConsent(true, sigData)} 
              onDecline={() => setCurrentView('settings')} 
            />
          )}

          {/* Scrollable Body & Footer Container */}
          <div className="flex-1 overflow-y-auto min-h-0 flex flex-col relative" id="scrollable-content-area">
            {/* Dynamic Main Workspace Container */}
            <main className="flex-1 w-full flex flex-col pb-12">
              
              {/* Render child dashboard based on authorized identity role or view */}
              <div className="animate-fade-in duration-300">
                 {activeUser?.requiresPasswordChange ? (
                    <div className="flex-1 flex items-center justify-center p-6 lg:p-12 min-h-[60vh]">
                      <PasswordResetWizard />
                    </div>
                 ) : isProfileIncomplete(activeUser) ? (
                    <SetupWizard onComplete={() => setCurrentView('profile')} />
                 ) : currentView === 'profile' ? (
                    <React.Suspense fallback={<div>Loading Profile...</div>}>
                      <OrganizationProfile />
                    </React.Suspense>
                 ) : currentView === 'applications' ? (
                    <StudentApplications />
                 ) : (currentView === 'placements' || currentView === 'placement-detail') ? (
                    <PlacementsPage />
                 ) : currentView === 'opportunities' ? (
                    <OpportunitiesPage />
                 ) : currentView === 'interviews' ? (
                    <InterviewsPage />
                 ) : currentView === 'advisors' ? (
                    <AdvisorsPage />
                 ) : currentView === 'advisees' ? (
                    <AdviseesPage />
                 ) : currentView === 'foundations' ? (
                    <FoundationsPage />
                  ) : currentView === 'reports' ? (
                    <ReportsPage />
                 ) : currentView === 'users' ? (
                     <RegionalUsersPage />
                 ) : currentView === 'superadmin-users' ? (
                     <SuperadminUsersPage />
                  ) : currentView === 'messages' ? (
                    <MessagesPage />
                  ) : currentView === 'settings' ? (
                    <SettingsPage />
                 ) : (currentView === 'console' && activeUser.role === 'superadmin') ? (
                    <SuperadminConsole />
                 ) : (currentView === 'regional_console' && activeUser.role === 'regional_admin') ? (
                    <RegionalAdminConsole />
                 ) : (
                    <>
                       {activeUser.role === 'superadmin' && <SuperadminDashboard />}
                       {activeUser.role === 'regional_admin' && <RegionalAdminDashboard />}
                       {activeUser.role === 'advisor' && <AdvisorDashboard />}
                       {activeUser.role === 'organization' && <OrganizationDashboard />}
                       {activeUser.role === 'student' && <StudentDashboard />}
                       {activeUser.role === 'parent' && <ParentDashboard />}
                    </>
                 )}
              </div>

            </main>

            {/* Corporate platform footer */}
            <GlobalFooter />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


export default function App() {
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.location.hostname === 'thespartanburgedge.com') {
        window.location.replace('https://www.thespartanburgedge.com' + window.location.pathname + window.location.search + window.location.hash);
      }
      
      // Guarded recovery: Clear the chunk load error reload flag after successful initialization
      const timer = setTimeout(() => {
        sessionStorage.removeItem('chunk-load-error-reload');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <InAppDialogProvider>
      <WBLProvider>
        <ThemeManager />
        <div className="flex flex-col min-h-screen">
          <DashboardRouter />
        </div>
        <ProxyFloatingIndicator />
      </WBLProvider>
    </InAppDialogProvider>
  );
}
